import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export function FieldWrapper({
  label,
  hint,
  error,
  required,
  id,
  children,
}: {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  id?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className="text-xs font-medium text-fg-muted">
          {label}
          {required && <span className="ml-0.5 text-gold-500">*</span>}
        </label>
      )}
      {children}
      {hint && !error && <p className="text-xs text-fg-subtle">{hint}</p>}
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'flex h-10 w-full rounded-md border bg-bg-elevated px-3 text-sm text-fg transition-colors',
        'placeholder:text-fg-subtle focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20',
        'disabled:cursor-not-allowed disabled:opacity-50',
        invalid ? 'border-danger/60' : 'border-border',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  ({ className, invalid, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        'flex min-h-[100px] w-full rounded-md border bg-bg-elevated px-3 py-2.5 text-sm text-fg transition-colors',
        'placeholder:text-fg-subtle focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20',
        invalid ? 'border-danger/60' : 'border-border',
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';